/**
 * Lunar mission ephemeris chain, ported from loader-globe.js 765-1008 and the
 * moon-marker sources at 1108-1175. For a lunar mission (orbit.type 'lunar' or
 * a horizonsId) the mission body and the Moon (301) are fetched together: the
 * Artemis II body (-1024) tries the NASA AROW OEM first, then JPL Horizons
 * (5 min step, now-3h to now+12d clamped to the mission window), and any
 * failure of the pair falls back to the bundled ephemeris. The Moon marker
 * takes live Horizons 301 samples, else the bundled moon file, else null for
 * the globe's Simon 1994 model. The runtime guards the chain with a
 * generation counter so a network switch discards late results.
 */
import { loaderApi } from '../data/api';
import { loadArtemisEphemeris, loadMoonEphemeris, staticFile } from '../data/staticData';
import type { EphemerisFile, EphemerisSample, Mission } from '../types';
import { fetchHorizonsVectors } from './horizons';
import type { LunarSource, MoonSource } from './store';
import { errorMessage } from './util';

export const ARTEMIS_HORIZONS_ID = '-1024';
export const MOON_HORIZONS_ID = '301';
export const LUNAR_STEP = '5 min';
export const LUNAR_LOOKBACK_MS = 3 * 3_600_000;
export const LUNAR_LOOKAHEAD_MS = 12 * 86_400_000;
/** Horizons has Artemis II vectors only inside the mission window. */
export const ARTEMIS_WINDOW_START_MS = Date.parse('2026-04-02T02:00:00Z');
export const ARTEMIS_WINDOW_END_MS = Date.parse('2026-04-10T23:00:00Z');

export function isLunarMission(mission: Mission): boolean {
  const orbit = mission.orbit;
  if (!orbit) return false;
  return orbit.type === 'lunar' || (orbit.horizonsId !== undefined && orbit.horizonsId !== null && orbit.horizonsId !== '');
}

export function missionHorizonsId(mission: Mission): string {
  const id = mission.orbit?.horizonsId;
  return id === undefined || id === null || id === '' ? ARTEMIS_HORIZONS_ID : String(id);
}

/** 'YYYY-MM-DD HH:MM', the format loader-globe.js sent for the lunar fetches. */
export function formatHorizonsMinute(ms: number): string {
  const d = new Date(ms);
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
}

/** now-3h to now+12d, clamped to the Artemis II window for -1024; null when the clamp leaves nothing. */
export function horizonsWindow(horizonsId: string, nowMs: number): { startMs: number; endMs: number } | null {
  let startMs = nowMs - LUNAR_LOOKBACK_MS;
  let endMs = nowMs + LUNAR_LOOKAHEAD_MS;
  if (horizonsId === ARTEMIS_HORIZONS_ID) {
    if (startMs < ARTEMIS_WINDOW_START_MS) startMs = ARTEMIS_WINDOW_START_MS;
    if (endMs > ARTEMIS_WINDOW_END_MS) endMs = ARTEMIS_WINDOW_END_MS;
    if (startMs >= endMs) return null;
  }
  return { startMs, endMs };
}

/**
 * CCSDS OEM text to samples: after META_STOP each data line is
 * "epoch X Y Z VX VY VZ" (km, km/s). Returns null when the text carries no
 * OEM markers or no data line parses, as the original did.
 */
export function parseOem(text: string): EphemerisSample[] | null {
  if (!text || typeof text !== 'string') return null;
  if (!text.includes('META_START') && !text.includes('CCSDS_OEM')) return null;
  const samples: EphemerisSample[] = [];
  let inData = false;
  let pastMeta = false;
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line || line.charAt(0) === '#') continue;
    if (line === 'META_START') {
      inData = false;
      pastMeta = false;
      continue;
    }
    if (line === 'META_STOP') {
      pastMeta = true;
      inData = true;
      continue;
    }
    if (line.indexOf('=') !== -1 && !pastMeta) continue;
    if (line.indexOf('CCSDS_OEM') !== -1) continue;
    if (line === 'COMMENT' || line.indexOf('COMMENT ') === 0) continue;
    if (!inData) continue;
    const parts = line.split(/\s+/);
    if (parts.length < 7) continue;
    let epoch = parts[0];
    if (epoch.indexOf('20') !== 0) continue;
    if (!epoch.endsWith('Z')) epoch += 'Z';
    const x = parseFloat(parts[1]);
    const y = parseFloat(parts[2]);
    const z = parseFloat(parts[3]);
    if (Number.isNaN(x) || Number.isNaN(y) || Number.isNaN(z)) continue;
    samples.push({ t: epoch, x, y, z, vx: parseFloat(parts[4]), vy: parseFloat(parts[5]), vz: parseFloat(parts[6]) });
  }
  return samples.length === 0 ? null : samples;
}

/** The bundled ephemeris for a mission: orbit.ephemerisUrl with the old json/loader/ prefix mapped to /data/loader/, else the Artemis II file. */
export function staticEphemerisUrl(mission: Mission): string | null {
  const url = mission.orbit?.ephemerisUrl;
  if (!url) return null;
  if (/^https?:\/\//.test(url) || url.startsWith('/')) return url;
  return '/' + url.replace(/^json\/loader\//, 'data/loader/');
}

export interface LunarSources {
  arowOem: () => Promise<string>;
  horizons: (command: string, startIso: string, stopIso: string, step: string) => Promise<string>;
  staticEphemeris: (mission: Mission) => Promise<EphemerisFile>;
  staticMoon: () => Promise<EphemerisFile>;
  nowMs: () => number;
}

export const defaultLunarSources: LunarSources = {
  arowOem: () => loaderApi.arowOem(),
  horizons: (command, startIso, stopIso, step) => loaderApi.horizons(command, startIso, stopIso, step),
  staticEphemeris: (mission) => {
    const url = staticEphemerisUrl(mission);
    if (!url) return loadArtemisEphemeris();
    return staticFile<EphemerisFile>(url.replace(/^\/data\/loader\//, ''));
  },
  staticMoon: () => loadMoonEphemeris(),
  nowMs: () => Date.now(),
};

export interface LunarResult {
  samples: EphemerisSample[];
  source: LunarSource;
  moonSamples: EphemerisSample[] | null;
  moonSource: MoonSource;
}

interface LiveEphemeris {
  samples: EphemerisSample[];
  source: LunarSource;
}

async function fetchHorizons(horizonsId: string, sources: LunarSources): Promise<LiveEphemeris> {
  const window = horizonsWindow(horizonsId, sources.nowMs());
  if (!window) throw new Error('Mission window outside range');
  const samples = await fetchHorizonsVectors(horizonsId, window.startMs, window.endMs, LUNAR_STEP, sources.horizons, formatHorizonsMinute);
  return { samples, source: 'horizons' };
}

/** AROW OEM first for -1024 (then Horizons on any failure), Horizons alone for other bodies. */
export async function fetchLiveEphemeris(horizonsId: string, sources: LunarSources): Promise<LiveEphemeris> {
  if (horizonsId !== ARTEMIS_HORIZONS_ID) return fetchHorizons(horizonsId, sources);
  try {
    const parsed = parseOem(await sources.arowOem());
    if (parsed) return { samples: parsed, source: 'arow' };
    console.warn('[lunar] AROW OEM did not parse; falling back to Horizons');
  } catch (err) {
    console.warn('[lunar] AROW OEM unavailable; falling back to Horizons:', errorMessage(err));
  }
  return fetchHorizons(horizonsId, sources);
}

async function moonFromStatic(sources: LunarSources): Promise<{ samples: EphemerisSample[] | null; source: MoonSource }> {
  try {
    const file = await sources.staticMoon();
    if (file && Array.isArray(file.samples) && file.samples.length > 0) return { samples: file.samples, source: 'static' };
    console.warn('[lunar] bundled moon ephemeris has no samples; using the Simon 1994 model');
  } catch (err) {
    console.warn('[lunar] moon ephemeris not available, using the Simon 1994 model:', errorMessage(err));
  }
  return { samples: null, source: 'simon' };
}

/**
 * The chain. Live: Promise.all of the mission body and the Moon; a rejection
 * of either (as in the original) or an empty body falls back to the bundled
 * ephemeris with the moon from the bundled file. Throws when the bundled
 * ephemeris fails too.
 */
export async function loadLunarEphemeris(mission: Mission, sources: LunarSources = defaultLunarSources): Promise<LunarResult> {
  const horizonsId = missionHorizonsId(mission);
  // A completed mission must never silently become today's AROW feed or an
  // out-of-window request. The reviewed post-flight file has explicit gaps.
  if (horizonsId === ARTEMIS_HORIZONS_ID && sources === defaultLunarSources) {
    const file = await sources.staticEphemeris(mission);
    if (!file?.samples?.length) throw new Error('Artemis historical trajectory is unavailable');
    const moon = await moonFromStatic(sources);
    return { samples: file.samples, source: 'cached', moonSamples: moon.samples, moonSource: moon.source };
  }
  let live: { body: LiveEphemeris; moon: LiveEphemeris } | null = null;
  try {
    const [body, moon] = await Promise.all([fetchLiveEphemeris(horizonsId, sources), fetchHorizons(MOON_HORIZONS_ID, sources)]);
    if (body.samples.length === 0) throw new Error('No live mission ephemeris samples');
    live = { body, moon };
  } catch (err) {
    console.warn(`[lunar] live ephemeris for ${mission.id} unavailable, using the bundled ephemeris:`, errorMessage(err));
  }

  if (live) {
    const moon =
      live.moon.samples.length > 0
        ? { samples: live.moon.samples, source: 'horizons' as MoonSource }
        : await moonFromStatic(sources);
    return { samples: live.body.samples, source: live.body.source, moonSamples: moon.samples, moonSource: moon.source };
  }

  const file = await sources.staticEphemeris(mission);
  if (!file || !Array.isArray(file.samples) || file.samples.length === 0) {
    throw new Error(`Bundled ephemeris for ${mission.id} has no samples`);
  }
  const moon = await moonFromStatic(sources);
  return { samples: file.samples, source: 'cached', moonSamples: moon.samples, moonSource: moon.source };
}
